import { t } from 'i18next';

import { ExternalIntegration } from '@hooks/useIntegrationConfigs';
import { ApplicationView, HowToHelpType, OnlineHelp, VolunteerMode } from '@shared/enums';
import { SimpleApplication, ApplicationItem } from '@widgets/cabinet/applicationCard/types';

import { groupBy, sortBy } from './arrays';

const categoryEmojis: Record<string, string> = {
  medicines: '💊',
  foodstuff: '🍞',
  fuel: '⛽',
  clothes: '🧥',
  hygiene: '🧼',
  'household-items': '🚪',
  'baby-products': '👶',
  stationery: '📏',
  'santa-claus-letter': '🎅',
};

export function getPrettyCategoryName(category: string) {
  const emoji = categoryEmojis[category] || '';

  return `${emoji ? `${emoji} ` : ''}${t(category)}`;
}

export function isOnlineCategory(category: HowToHelpType): category is OnlineHelp {
  return [
    HowToHelpType.OnlineFood,
    HowToHelpType.OnlineMedicaments,
    HowToHelpType.OnlineFuel,
    HowToHelpType.SantaClausLetter,
  ].includes(category);
}

export function isOnlineMode(mode: VolunteerMode): boolean {
  return mode === VolunteerMode.Online;
}

export function isDefaultView(view: ApplicationView): boolean {
  return view === ApplicationView.Default;
}

export const groupApplicationItemsByCategory = (items: ApplicationItem[]) => {
  const grouped = groupBy(items, (item) => item.category.name);

  const itemsByCategory = Object.entries(grouped).map(([category, items]) => ({
    category,
    items,
  }));

  return sortBy(itemsByCategory, ({ items }) => -items.length);
};

export const groupApplicationItemsByIntegration = (
  application: SimpleApplication,
  integrations: ExternalIntegration[],
) => {
  const { items, shops } = application;

  const result = [];

  for (const shop of shops) {
    const integration = integrations.find((item) => item.type === shop.type);
    if (!integration) {
      continue;
    }

    const integrationShop = integration.shops.find((item) => item.name === shop.name);
    if (!integrationShop) {
      continue;
    }

    const integrationApplicationItems = items.filter(({ category }) =>
      integrationShop.config.applicationCategories.includes(category.name),
    );

    if (integrationApplicationItems.length > 0) {
      result.push({
        integration,
        application,
        items: integrationApplicationItems,
      });
    }
  }

  return sortBy(result, ({ items }) => -items.length);
};
